import type { ArchSlug } from './tabs'

/**
 * The facts behind the interactive system map on the Architecture overview
 * (z8nrz7f3kc). Pure data: the map component only lays it out. Everything
 * here restates the Architecture pages — change a fact there and here together.
 */

export type NodeId = 'ci' | 'frontend' | 'api' | 'mock' | 'mllp' | 'db' | 'partner'

/** Matches a key of `COLORS` in `diagrams/svg-primitives.tsx`. */
export type NodeKind = NodeId

export type EnvId = 'dev' | 'staging' | 'medstar-health' | 'prod'

export interface SystemNode {
  id: NodeId
  title: string
  /** One-line role, shown under the title on the map. */
  subtitle: string
  description: string
  /** Repository that ships it, or null for managed infrastructure / external systems. */
  repo: string | null
  ports: string
  /** Where to read more. */
  tab: ArchSlug
  /** Outside every environment's boundary (CI/CD, the partner's EHR). */
  external?: boolean
}

export interface SystemEdge {
  from: NodeId
  to: NodeId
  label: string
  dashed?: boolean
}

export interface SystemEnv {
  id: EnvId
  label: string
  status: 'active' | 'provisioned'
  statusNote: string
  webDomain: string
  apiDomain: string
  network: string
  compute: string
  ciTrigger: string
  /** Nodes deployed in (or connected to) this environment. */
  nodes: readonly NodeId[]
  /** Per-environment overrides of a node's map subtitle. */
  subtitles?: Partial<Record<NodeId, string>>
}

export const SYSTEM_NODES: Record<NodeId, SystemNode> = {
  ci: {
    id: 'ci',
    title: 'GitHub Actions',
    subtitle: 'CI / CD',
    description:
      'Builds and deploys the API and the web app on push. Authenticates to AWS through GitHub OIDC and an IAM role, so no long-lived access keys are stored anywhere.',
    repo: null,
    ports: '—',
    tab: 'architecture-environments',
    external: true,
  },
  frontend: {
    id: 'frontend',
    title: 'CGT Frontend',
    subtitle: 'React 19 SPA',
    description:
      'The React 19 single-page app. A static build in a private S3 bucket, served through CloudFront. No servers to run.',
    repo: 'cgt-frontend',
    ports: '443 via CloudFront',
    tab: 'architecture-deployment',
  },
  api: {
    id: 'api',
    title: 'CGT API',
    subtitle: 'NestJS · port 3000',
    description:
      'The NestJS REST + WebSocket API. Validates the Cognito JWT on every request and scopes it to the caller’s tenant via X-Tenant-Id.',
    repo: 'cgt-backend',
    ports: '3000',
    tab: 'architecture-api-security',
  },
  mock: {
    id: 'mock',
    title: 'Integration Service',
    subtitle: 'Mock sub-service · port 4000',
    description:
      'The Mock sub-service of the Integration Service: synthetic instrument and HL7 fixtures, called server-to-server by the API.',
    repo: 'integrations-service',
    ports: '4000',
    tab: 'architecture-environments',
  },
  mllp: {
    id: 'mllp',
    title: 'MLLP Data Layer',
    subtitle: 'Ports 6001-6003, 8080',
    description:
      'The Integration Service sub-service that carries real HL7 traffic. Receives ADT, ORM and ORU over MLLP and acknowledges each message; the API reads it over loopback :8080.',
    repo: 'integrations-service',
    ports: '6001-6003 (MLLP), 8080 (status API)',
    tab: 'architecture-hl7-integration',
  },
  db: {
    id: 'db',
    title: 'PostgreSQL',
    subtitle: 'RDS',
    description:
      'Amazon RDS for PostgreSQL 16 in a private data subnet. Encrypted at rest, automated backups, reachable only from the API’s security group.',
    repo: null,
    ports: '5432',
    tab: 'architecture-aws-topology',
  },
  partner: {
    id: 'partner',
    title: 'MedStar EHR',
    subtitle: 'Epic · Beacon · Beaker',
    description:
      'MedStar’s Epic systems. Send HL7 v2 over MLLP across the site-to-site VPN into the MLLP Data Layer.',
    repo: null,
    ports: 'VPN → 6001-6003',
    tab: 'architecture-hl7-integration',
    external: true,
  },
}

export const SYSTEM_EDGES: readonly SystemEdge[] = [
  { from: 'frontend', to: 'api', label: 'HTTPS · JWT' },
  { from: 'api', to: 'db', label: 'Prisma' },
  { from: 'api', to: 'mock', label: 'server-to-server', dashed: true },
  { from: 'api', to: 'mllp', label: 'loopback :8080', dashed: true },
  { from: 'partner', to: 'mllp', label: 'HL7 over MLLP' },
  { from: 'ci', to: 'api', label: 'deploy', dashed: true },
  { from: 'ci', to: 'frontend', label: 'deploy', dashed: true },
]

const CORE: readonly NodeId[] = ['ci', 'frontend', 'api', 'mock', 'db']

export const SYSTEM_ENVS: readonly SystemEnv[] = [
  {
    id: 'dev',
    label: 'dev',
    status: 'active',
    statusNote: 'Active',
    webDomain: 'app.dev.smart-cgt.com',
    apiDomain: 'api.dev.smart-cgt.com',
    network: 'New VPC, 10.2.0.0/16',
    compute: 'ECS Fargate, 1-2 tasks',
    ciTrigger: 'push to dev',
    nodes: CORE,
  },
  {
    id: 'staging',
    label: 'staging',
    status: 'active',
    statusNote: 'Active',
    webDomain: 'app.stag.smart-cgt.com',
    apiDomain: 'api.stag.smart-cgt.com',
    network: 'New VPC, 10.0.0.0/16',
    compute: 'ECS Fargate, 1-2 tasks',
    ciTrigger: 'push to staging',
    nodes: CORE,
  },
  {
    id: 'medstar-health',
    label: 'medstar-health',
    status: 'active',
    statusNote: 'Active — MedStar’s dedicated, VPN-connected environment',
    webDomain: 'app.medstar-health.smart-cgt.com',
    apiDomain: 'api.medstar-health.smart-cgt.com',
    network: 'Adopted VPC + peered georgetown-test VPC',
    compute: 'ECS-on-EC2, fixed IP 172.16.1.100',
    ciTrigger: 'push to main / dispatch',
    nodes: [...CORE, 'mllp', 'partner'],
    subtitles: { api: 'NestJS · ECS-on-EC2, fixed IP' },
  },
  {
    id: 'prod',
    label: 'prod',
    status: 'provisioned',
    statusNote: 'Provisioned in Terraform, no CI/CD wired up yet',
    webDomain: 'app.smart-cgt.com',
    apiDomain: 'api.smart-cgt.com',
    network: 'New VPC, 10.1.0.0/16',
    compute: 'ECS Fargate, 2-10 tasks',
    ciTrigger: 'none yet (manual tofu apply)',
    // No pipeline deploys prod, so CI/CD is not part of its picture.
    nodes: ['frontend', 'api', 'mock', 'db'],
  },
]

export function findEnv(id: EnvId): SystemEnv {
  return SYSTEM_ENVS.find(e => e.id === id) ?? SYSTEM_ENVS[0]
}

export function hasNode(env: SystemEnv, id: NodeId) {
  return env.nodes.includes(id)
}

/** An edge is drawn live only when both ends are deployed in the environment. */
export function edgeActive(env: SystemEnv, edge: SystemEdge) {
  return hasNode(env, edge.from) && hasNode(env, edge.to)
}

export function nodeSubtitle(env: SystemEnv, id: NodeId) {
  return env.subtitles?.[id] ?? SYSTEM_NODES[id].subtitle
}

/** The node and every edge touching it — what to keep lit while it is focused. */
export function neighbourhood(id: NodeId): { nodes: Set<NodeId>; edges: Set<number> } {
  const nodes = new Set<NodeId>([id])
  const edges = new Set<number>()
  SYSTEM_EDGES.forEach((e, i) => {
    if (e.from === id || e.to === id) {
      edges.add(i)
      nodes.add(e.from)
      nodes.add(e.to)
    }
  })
  return { nodes, edges }
}

/** What stands in for a repository on nodes no repo of ours ships. */
const NO_REPO: Partial<Record<NodeId, string>> = {
  db: 'Provisioned by cgt-infra (Terraform)',
  ci: 'GitHub-hosted, workflows in each repo',
  partner: 'External — MedStar',
}

/** The facts a node's detail panel lists, for the selected environment. */
export function nodeFacts(env: SystemEnv, id: NodeId): { label: string; value: string }[] {
  const node = SYSTEM_NODES[id]
  const facts = [
    { label: 'Repository', value: node.repo ?? NO_REPO[id] ?? 'External' },
    { label: 'Ports', value: node.ports },
  ]
  if (id === 'frontend') facts.push({ label: `Domain (${env.label})`, value: env.webDomain })
  if (id === 'api') {
    facts.push({ label: `Domain (${env.label})`, value: env.apiDomain })
    facts.push({ label: 'Compute', value: env.compute })
  }
  if (id === 'ci') facts.push({ label: `Trigger (${env.label})`, value: env.ciTrigger })
  return facts
}
